import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { toast } from 'react-toastify';
import { ArrowLeft, Save, Send, ChevronDown, Clock, User, RotateCcw, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { Mark, mergeAttributes } from '@tiptap/core';
import './KnowledgeArticleEditorPage.css';

const STATUS_LABELS = {
    draft:     'Черновик',
    review:    'На проверке',
    revision:  'На доработке',
    published: 'Опубликован',
};

const STATUS_COLORS = {
    draft:     'status-draft',
    review:    'status-review',
    revision:  'status-revision',
    published: 'status-published',
};

function formatDateTime(dt) {
    if (!dt) return '';
    const raw = String(dt).trim();
    const withZone = /[zZ]$|[+\-]\d{2}:\d{2}$/.test(raw)
        ? raw
        : `${raw.replace(' ', 'T')}Z`;
    return new Date(withZone).toLocaleString('ru-RU', {
        timeZone: 'Europe/Moscow',
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
}

const FontSize = Mark.create({
    name: 'fontSize',
    addAttributes() {
        return {
            size: {
                default: null,
                parseHTML: (element) => element.style.fontSize || null,
                renderHTML: (attributes) => {
                    if (!attributes.size) return {};
                    return { style: `font-size: ${attributes.size}` };
                }
            }
        };
    },
    parseHTML() {
        return [{ tag: 'span[style*=font-size]' }];
    },
    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes(HTMLAttributes), 0];
    },
    addCommands() {
        return {
            setFontSize: (size) => ({ chain }) => chain().setMark(this.name, { size }).run(),
            unsetFontSize: () => ({ chain }) => chain().unsetMark(this.name).run(),
        };
    }
});

// ─── TipTap Toolbar ────────────────────────────────────────────────
const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
    <button
        type="button"
        className={`tiptap-btn ${active ? 'tiptap-btn-active' : ''}`}
        onClick={onClick}
        disabled={disabled}
        title={title}
    >
        {children}
    </button>
);

const EditorToolbar = ({ editor, onImageUpload, uploading }) => {
    if (!editor) return null;
    const fontSize = editor.getAttributes('fontSize')?.size || '20px';

    const setLink = () => {
        const url = window.prompt('Введите URL ссылки:');
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkToLink({ href: '' }).unsetLink().run();
            return;
        }
        editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
    };

    return (
        <div className="tiptap-toolbar">
            {/* Text style */}
            <div className="tiptap-toolbar-group">
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    active={editor.isActive('bold')}
                    title="Жирный (Ctrl+B)"
                ><b>B</b></ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    active={editor.isActive('italic')}
                    title="Курсив (Ctrl+I)"
                ><i>I</i></ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    active={editor.isActive('underline')}
                    title="Подчёркнутый (Ctrl+U)"
                ><u>U</u></ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    active={editor.isActive('strike')}
                    title="Зачёркнутый"
                ><s>S</s></ToolbarButton>
            </div>

            {/* Headings */}
            <div className="tiptap-toolbar-group">
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    active={editor.isActive('heading', { level: 2 })}
                    title="Заголовок H2"
                >H2</ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    active={editor.isActive('heading', { level: 3 })}
                    title="Заголовок H3"
                >H3</ToolbarButton>
            </div>

            {/* Font size */}
            <div className="tiptap-toolbar-group">
                <select
                    className="tiptap-font-size-select"
                    value={fontSize}
                    onChange={(e) => {
                        const value = e.target.value;
                        editor.chain().focus().setFontSize(value).run();
                    }}
                    title="Размер шрифта"
                >
                    <option value="20px">20px (рекомендованно)</option>
                    <option value="14px">14px</option>
                    <option value="16px">16px</option>
                    <option value="18px">18px</option>
                    <option value="24px">24px</option>
                    <option value="28px">28px</option>
                </select>
            </div>

            {/* Lists */}
            <div className="tiptap-toolbar-group">
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    active={editor.isActive('bulletList')}
                    title="Маркированный список"
                >• —</ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    active={editor.isActive('orderedList')}
                    title="Нумерованный список"
                >1. —</ToolbarButton>
            </div>

            {/* Alignment */}
            <div className="tiptap-toolbar-group">
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    active={editor.isActive({ textAlign: 'left' })}
                    title="По левому краю"
                >⬅</ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    active={editor.isActive({ textAlign: 'center' })}
                    title="По центру"
                >⬛</ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    active={editor.isActive({ textAlign: 'right' })}
                    title="По правому краю"
                >➡</ToolbarButton>
            </div>

            {/* Misc */}
            <div className="tiptap-toolbar-group">
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    active={editor.isActive('blockquote')}
                    title="Цитата"
                >❝</ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    active={editor.isActive('codeBlock')}
                    title="Блок кода"
                >{'</>'}</ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    title="Разделитель"
                >—</ToolbarButton>
                <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Ссылка">
                    🔗
                </ToolbarButton>
            </div>

            {/* Image */}
            <div className="tiptap-toolbar-group">
                <label className={`tiptap-btn tiptap-image-btn ${uploading ? 'disabled' : ''}`} title="Вставить изображение">
                    <ImageIcon size={15} />
                    <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={onImageUpload}
                        disabled={uploading}
                    />
                </label>
            </div>

            {/* Undo/Redo */}
            <div className="tiptap-toolbar-group">
                <ToolbarButton
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    title="Отменить (Ctrl+Z)"
                >↩</ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    title="Повторить (Ctrl+Y)"
                >↪</ToolbarButton>
            </div>
        </div>
    );
};

// ─── Status History ────────────────────────────────────────────────
const StatusHistory = ({ history }) => {
    if (!history || history.length === 0) return null;
    return (
        <div className="kae-history">
            <h3 className="kae-history-title">
                <Clock size={15} />
                История статусов
            </h3>
            <ul className="kae-history-list">
                {history.map(h => (
                    <li key={h.id} className="kae-history-item">
                        <span className={`ke-status ${STATUS_COLORS[h.status]}`}>
                            {STATUS_LABELS[h.status]}
                        </span>
                        <span className="kae-history-meta">
                            <User size={12} />{h.changed_by_name}
                        </span>
                        <span className="kae-history-meta">
                            <Clock size={12} />{formatDateTime(h.changed_at)}
                        </span>
                        {h.comment && <span className="kae-history-comment">«{h.comment}»</span>}
                    </li>
                ))}
            </ul>
        </div>
    );
};

// ─── Main component ────────────────────────────────────────────────
const KnowledgeArticleEditorPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const isNew = !id;

    const [title, setTitle] = useState('');
    const [editorIds, setEditorIds] = useState([]);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [statusComment, setStatusComment] = useState('');
    const [showStatusPanel, setShowStatusPanel] = useState(false);

    // TipTap editor
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            FontSize,
            Image.configure({ inline: false, allowBase64: true }),
            Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Placeholder.configure({ placeholder: 'Начните писать статью...' }),
        ],
        content: '',
        editorProps: {
            attributes: { class: 'tiptap-content' }
        }
    });

    // Load existing article
    const { data: article, isLoading } = useQuery(
        ['knowledgeArticleEdit', id],
        async () => {
            const res = await axios.get(`${API_BASE_URL}/knowledge/${id}`, { withCredentials: true });
            return res.data;
        },
        { enabled: !isNew, retry: false }
    );

    const { data: knowledgeEditors = [] } = useQuery(
        'knowledgeEditors',
        async () => {
            const res = await axios.get(`${API_BASE_URL}/knowledge/editors`, { withCredentials: true });
            return res.data;
        }
    );

    // Load status history
    const { data: history = [] } = useQuery(
        ['knowledgeHistory', id],
        async () => {
            const res = await axios.get(`${API_BASE_URL}/knowledge/${id}/history`, { withCredentials: true });
            return res.data;
        },
        { enabled: !isNew }
    );

    useEffect(() => {
        if (article && editor) {
            setTitle(article.title || '');
            setEditorIds(
                Array.isArray(article.editor_ids)
                    ? [...new Set(article.editor_ids.map((v) => parseInt(v, 10)).filter((v) => Number.isInteger(v) && v > 0))]
                    : []
            );
            editor.commands.setContent(article.content || '');
        }
    }, [article, editor]);

    // Для новой статьи базовый набор текста всегда 20px
    useEffect(() => {
        if (!editor || !isNew) return;
        editor.chain().focus().setFontSize('20px').run();
    }, [editor, isNew]);

    // Save (create or update)
    const handleSave = useCallback(async () => {
        if (!title.trim()) { toast.error('Введите заголовок'); return; }
        setSaving(true);
        try {
            const data = { title: title.trim(), content: editor?.getHTML() || '', editor_ids: editorIds };
            let result;
            if (isNew) {
                const res = await axios.post(`${API_BASE_URL}/knowledge`, data, { withCredentials: true });
                result = res.data;
                toast.success('Статья создана');
            } else {
                const res = await axios.put(`${API_BASE_URL}/knowledge/${id}`, data, { withCredentials: true });
                result = res.data;
                toast.success('Статья сохранена');
            }
            queryClient.invalidateQueries('knowledgeArticles');
            queryClient.invalidateQueries(['knowledgeArticleEdit', id]);
            if (isNew) navigate(`/knowledge/editor/edit/${result.id}`, { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    }, [title, editor, editorIds, isNew, id, navigate, queryClient]);

    // Change status
    const changeStatusMutation = useMutation(
        async ({ status, comment }) => {
            const res = await axios.put(
                `${API_BASE_URL}/knowledge/${id}/status`,
                { status, comment },
                { withCredentials: true }
            );
            return res.data;
        },
        {
            onSuccess: (data) => {
                toast.success(`Статус изменён: ${STATUS_LABELS[data.status]}`);
                queryClient.invalidateQueries('knowledgeArticles');
                queryClient.invalidateQueries(['knowledgeArticleEdit', id]);
                queryClient.invalidateQueries(['knowledgeHistory', id]);
                setStatusComment('');
                setShowStatusPanel(false);
            },
            onError: (err) => {
                toast.error(err.response?.data?.message || 'Ошибка смены статуса');
            }
        }
    );

    const handleChangeStatus = (newStatus) => {
        if (!id) { toast.error('Сначала сохраните статью'); return; }
        changeStatusMutation.mutate({ status: newStatus, comment: statusComment });
    };

    // Image upload
    const handleImageUpload = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await axios.post(`${API_BASE_URL}/knowledge/upload-image`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            editor?.chain().focus().setImage({ src: res.data.url }).run();
        } catch (err) {
            toast.error('Ошибка загрузки изображения');
        } finally {
            setUploading(false);
        }
    }, [editor]);

    if (!isNew && isLoading) {
        return <div className="kae-page"><div className="kae-loading"><div className="spinner" /></div></div>;
    }

    const currentStatus = article?.status || 'draft';
    const canSend = !isAdmin && (currentStatus === 'draft' || currentStatus === 'revision');
    const adminStatuses = ['draft', 'review', 'revision', 'published'];

    return (
        <div className="kae-page">
            <div className="kae-toolbar">
                <button className="kae-back-btn" onClick={() => navigate('/knowledge/editor')}>
                    <ArrowLeft size={18} />
                    К списку
                </button>
                <div className="kae-toolbar-right">
                    {!isNew && (
                        <span className={`ke-status ${STATUS_COLORS[currentStatus]}`}>
                            {STATUS_LABELS[currentStatus]}
                        </span>
                    )}
                    <button
                        className="kae-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        <Save size={16} />
                        {saving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                    {/* Отправить на проверку — для редактора */}
                    {!isNew && !isAdmin && canSend && (
                        <button
                            className="kae-send-btn"
                            onClick={() => handleChangeStatus('review')}
                            disabled={changeStatusMutation.isLoading}
                        >
                            <Send size={16} />
                            На проверку
                        </button>
                    )}
                    {/* Управление статусом — для админа */}
                    {!isNew && isAdmin && (
                        <div className="kae-status-dropdown">
                            <button
                                className="kae-status-btn"
                                onClick={() => setShowStatusPanel(v => !v)}
                            >
                                Статус <ChevronDown size={15} />
                            </button>
                            {showStatusPanel && (
                                <div className="kae-status-panel">
                                    <textarea
                                        className="kae-status-comment"
                                        placeholder="Комментарий (необязательно)"
                                        value={statusComment}
                                        onChange={e => setStatusComment(e.target.value)}
                                        rows={2}
                                    />
                                    <div className="kae-status-actions">
                                        {adminStatuses.filter(s => s !== currentStatus).map(s => (
                                            <button
                                                key={s}
                                                className={`kae-status-change-btn ${STATUS_COLORS[s]}`}
                                                onClick={() => handleChangeStatus(s)}
                                                disabled={changeStatusMutation.isLoading}
                                            >
                                                {STATUS_LABELS[s]}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="kae-main">
                <div className="kae-editor-section">
                    <input
                        type="text"
                        className="kae-title-input"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="Заголовок статьи"
                    />
                    <div className="kae-editors-panel">
                        <div className="kae-editors-title">Редакторы статьи</div>
                        <div className="kae-editors-list">
                            {knowledgeEditors.map((u) => (
                                <label key={u.id} className="kae-editor-option">
                                    <input
                                        type="checkbox"
                                        checked={editorIds.includes(Number(u.id))}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            const uid = Number(u.id);
                                            setEditorIds((prev) => (
                                                checked
                                                    ? [...new Set([...prev, uid])]
                                                    : prev.filter((idVal) => idVal !== uid)
                                            ));
                                        }}
                                    />
                                    <span>{u.name} ({u.email})</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="kae-editor-wrap">
                        <EditorToolbar editor={editor} onImageUpload={handleImageUpload} uploading={uploading} />
                        <EditorContent editor={editor} className="kae-editor" />
                    </div>
                </div>

                {!isNew && (
                    <div className="kae-sidebar">
                        <StatusHistory history={history} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default KnowledgeArticleEditorPage;
